// Conteúdo das páginas de serviço do alphaclean.pt
// Fonte única: alimenta o Header, o Footer, a Home, o QuoteForm e a página /servico/:id

export interface LimpezaFaq {
  question: string;
  answer: string;
}

export interface LimpezaStep {
  icon: string;
  title: string;
  description: string;
}

/**
 * Bloco de categoria apresentado com imagem (ou painel com ícone) de um lado e
 * texto do outro. Quando `image` não está preenchida, a página desenha um
 * painel com o ícone — basta acrescentar a foto depois para a substituir.
 */
export interface LimpezaCategoria {
  title: string;
  icon: string;
  description: string;
  image?: string;
  itens: string[];
}

export interface LimpezaService {
  id: string;
  slug: string;
  navTitle: string;
  formValue: string;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  imagePosition?: string;
  beneficios: string[];
  features: string[];
  fullContent: string;
  categoriasTitle?: string;
  categoriasSubtitle?: string;
  categorias?: LimpezaCategoria[];
  steps?: LimpezaStep[];
  faqs?: LimpezaFaq[];
}

export const limpezaServicesList: LimpezaService[] = [
  {
    id: 'limpeza-condominios',
    slug: 'limpeza-condominios',
    navTitle: 'Condomínios',
    formValue: 'Limpeza de Condomínios',
    title: 'Limpeza de Condomínios em Lisboa',
    subtitle: 'Partes comuns sempre apresentáveis, com equipa fixa e contrato mensal sem surpresas',
    description: 'Limpeza de partes comuns de prédios em Lisboa: escadas, hall, elevadores, garagens e zona de lixos. Equipa fixa e identificada, contrato mensal ou quinzenal e relatório para a administração.',
    image: '/images/limpeza-condominios.webp',
    imagePosition: 'center 40%',
    beneficios: [
      'Equipa fixa e identificada — os moradores conhecem quem entra',
      'Contrato mensal com valor fechado, sem extras a meio do ano',
      'Relatório de visitas para apresentar em assembleia',
      'Produtos, máquinas e consumíveis incluídos',
      'Empresa com seguro de responsabilidade civil'
    ],
    features: [
      'Lavagem e aspiração de escadas e patamares',
      'Limpeza do hall e zona de caixas de correio',
      'Higienização de elevadores, botões e corrimãos',
      'Vidros, portas e espelhos interiores',
      'Despejo e higienização da zona de contentores',
      'Varrimento e lavagem de garagens e rampas'
    ],
    fullContent: `A ALPHA CLEAN faz limpeza de condomínios em Lisboa para prédios de habitação, edifícios mistos e empreendimentos com várias entradas. A primeira impressão de um prédio começa na entrada: um hall com pó, um elevador com marcas ou uma escada por lavar dizem muito mais sobre a gestão do condomínio do que qualquer ata de assembleia.

O nosso serviço assenta em três decisões simples. A primeira é a equipa fixa: é sempre a mesma pessoa a limpar o seu prédio, identificada e conhecida dos moradores, porque quem já conhece o edifício repara no que mudou. A segunda é o valor fechado: a proposta é escrita, indica quantas visitas por semana, o que está incluído e quanto custa por mês — sem extras a meio do ano. A terceira é o registo: a administração recebe o mapa das visitas, útil quando é preciso prestar contas em assembleia.

O plano de limpeza de partes comuns inclui lavagem e aspiração de escadas e patamares, limpeza do hall de entrada e da zona de caixas de correio, higienização de elevadores com atenção aos botões e corrimãos, limpeza de vidros e portas interiores, remoção de teias de aranha em tetos e iluminação, despejo e higienização dos contentores e da zona de lixos, e varrimento e lavagem de garagens, rampas e arrecadações comuns.

A frequência certa depende do número de frações e do movimento do prédio. Num edifício pequeno de habitação, uma visita semanal chega para manter tudo apresentável. Em prédios com mais de vinte frações, com elevador muito utilizado ou com comércio no piso térreo, duas a três visitas por semana são o mínimo razoável. Para além do plano regular, fazemos limpezas profundas periódicas: lavagem mecânica de garagens, limpeza de fachadas e vidros em altura e desengorduramento de pavimentos.

Trabalhamos com administrações de condomínio profissionais e também diretamente com condomínios geridos pelos próprios moradores. Em qualquer dos casos fazemos visita ao edifício antes de orçamentar — só assim se sabe quantos pisos, que materiais e que zonas exteriores estão em causa. A visita e o orçamento são gratuitos e sem compromisso.`,
    categoriasTitle: 'Serviços complementares para condomínios',
    categoriasSubtitle: 'Além do plano regular, há trabalhos que um prédio precisa uma ou duas vezes por ano',
    categorias: [
      {
        title: 'Planos típicos',
        icon: 'ri-calendar-check-line',
        description: 'O plano ajusta-se ao movimento do edifício. Começamos pelo essencial e reforçamos se for preciso — não o contrário.',
        itens: [
          'Essencial — 1 visita por semana: escadas, hall e elevador',
          'Completo — 2 a 3 visitas por semana, com garagem e vidros',
          'Premium — visitas diárias e limpezas profundas trimestrais',
          'Contrato mensal ou quinzenal, sem fidelização longa'
        ]
      },
      {
        title: 'Lavagem de garagens e pavimentos',
        icon: 'ri-car-line',
        description: 'Garagens acumulam pó de travões, manchas de óleo e areia trazida pelos carros. Lavamos com máquina e produto próprio, incluindo rampas e lugares marcados.',
        itens: [
          'Lavagem mecânica de pavimento',
          'Desengorduramento de manchas de óleo',
          'Rampas, lugares e zonas de manobra',
          'Recomendada uma a duas vezes por ano'
        ]
      },
      {
        title: 'Fachadas e vidros em altura',
        icon: 'ri-building-2-line',
        description: 'Clarabóias, janelas de caixa de escada e vidros de fachada que ninguém alcança. Temos meios e seguro para trabalho em altura.',
        itens: [
          'Vidros de caixa de escada e clarabóias',
          'Limpeza de fachada imediata e entrada',
          'Trabalho em altura com seguro',
          'Agendado fora das horas de maior movimento'
        ]
      },
      {
        title: 'Gestão de resíduos e ecopontos',
        icon: 'ri-recycle-line',
        description: 'A zona de lixos é o ponto onde um condomínio se degrada mais depressa. Lavamos contentores, desinfetamos o espaço e organizamos a separação.',
        itens: [
          'Lavagem e desinfeção de contentores',
          'Organização da separação de resíduos',
          'Controlo de odores',
          'Colocação e recolha em dia de recolha municipal'
        ]
      }
    ],
    steps: [
      { icon: 'ri-home-4-line', title: 'Visita ao edifício', description: 'Vemos os pisos, as zonas exteriores e a garagem para perceber o trabalho real.' },
      { icon: 'ri-file-text-line', title: 'Proposta escrita', description: 'Recebe o plano de visitas, o que está incluído e o valor mensal fechado.' },
      { icon: 'ri-team-line', title: 'Equipa atribuída', description: 'Fica com uma equipa fixa e identificada, sempre a mesma no seu prédio.' },
      { icon: 'ri-eye-line', title: 'Acompanhamento', description: 'Supervisão regular e mapa de visitas para a administração.' }
    ],
    faqs: [
      { question: 'Qual é a frequência ideal para um prédio de habitação?', answer: 'Num prédio pequeno, uma visita semanal costuma chegar. Acima de vinte frações, com elevador muito usado ou comércio no rés do chão, recomendamos duas a três visitas por semana. Avaliamos na visita e dizemos-lhe o que é realmente necessário.' },
      { question: 'Os produtos e o material são incluídos?', answer: 'São. Levamos produtos, panos, esfregonas, aspiradores e máquinas. Consumíveis de casas de banho comuns podem ser incluídos no contrato, se o condomínio quiser.' },
      { question: 'Trabalham com administrações de condomínio?', answer: 'Sim, com administrações profissionais e também com condomínios geridos pelos próprios moradores. Emitimos fatura com NIF e entregamos mapa de visitas para apresentar em assembleia.' },
      { question: 'E se for preciso uma limpeza extra fora do plano?', answer: 'Orçamentamos à parte e só avançamos com aprovação. Limpezas de garagem, fachadas ou pós-obra de uma fração são exemplos típicos de trabalho pontual.' },
      { question: 'Há fidelidade no contrato?', answer: 'Não exigimos contratos longos. Trabalhamos com renovação mensal e pré-aviso curto — quem fica, fica porque quer.' }
    ]
  },
  {
    id: 'limpeza-pos-obra',
    slug: 'limpeza-pos-obra',
    navTitle: 'Pós-Obra',
    formValue: 'Limpeza Pós-Obra',
    title: 'Limpeza Pós-Obra em Lisboa',
    subtitle: 'Tiramos o pó fino, a cola, a tinta e o cimento — o espaço fica pronto a entregar',
    description: 'Limpeza pós-obra em Lisboa para apartamentos, moradias, lojas e escritórios. Aspiração industrial de pó fino, remoção de cimento, cola e tinta, limpeza de vidros e entrega pronta a habitar.',
    image: '/images/limpeza-pos-obra.webp',
    imagePosition: 'center 45%',
    beneficios: [
      'Espaço entregue pronto a habitar ou a abrir',
      'Aspiração industrial com filtro — o pó fino não volta',
      'Remoção de cimento, cola, silicone e tinta sem danificar acabamentos',
      'Orçamento fechado por m² ou por empreitada',
      'Possibilidade de trabalho ao fim de semana para cumprir prazos'
    ],
    features: [
      'Recolha de entulho leve e embalagens',
      'Aspiração de pó em paredes, tetos e pavimentos',
      'Remoção de restos de cimento, gesso e argamassa',
      'Remoção de cola, silicone e respingos de tinta',
      'Vidros, caixilhos e calhas de janela',
      'Lavagem final com produto adequado a cada material'
    ],
    fullContent: `A limpeza pós-obra é o trabalho que faz a obra parecer acabada. Por muito bem executada que esteja a empreitada, um apartamento entregue com pó fino nos rodapés, cola nos vidros e cimento no pavimento passa a sensação de trabalho por terminar. A ALPHA CLEAN faz limpeza pós-obra em Lisboa para remodelações de apartamentos, moradias, lojas, escritórios e empreendimentos novos.

O pó de obra não é pó normal. É um pó finíssimo de gesso, cimento e madeira que entra em todas as frestas, assenta outra vez horas depois de varrido e volta a levantar-se sempre que se abre uma janela. Por isso não se limpa com vassoura: usamos aspiração industrial com filtro, trabalhamos de cima para baixo — tetos, paredes, caixilhos, mobiliário, pavimento — e repetimos a passagem depois de o pó assentar. É essa segunda passagem que distingue uma limpeza pós-obra a sério de uma limpeza normal feita depois da obra.

O serviço inclui recolha de entulho leve e embalagens deixadas em obra, aspiração de pó em paredes, tetos e pavimentos, remoção de restos de cimento, gesso e argamassa, remoção de cola, silicone e respingos de tinta, limpeza de vidros, caixilhos e calhas de janela, limpeza interior e exterior de armários e roupeiros, desengorduramento da cozinha e lavagem completa das casas de banho, limpeza de portas, aduelas, rodapés e interruptores, e lavagem final de pavimentos com o produto adequado ao material.

Esse último ponto é mais importante do que parece. Tirar cimento de um soalho flutuante, de uma pedra natural ou de um microcimento exige produtos e métodos diferentes — e o produto errado deixa uma marca permanente que só se resolve voltando a tratar o pavimento. Identificamos os materiais antes de começar e escolhemos em função disso.

Orçamentamos por m² em apartamentos e lojas, ou por empreitada em obras grandes e em empreendimentos com várias frações. Em obras longas, o mais eficiente é dividir em duas fases: uma limpeza grossa durante a obra, que permite trabalhar melhor, e uma limpeza fina na entrega. Trabalhamos ao fim de semana sempre que a data de entrega o exige — e a vistoria final é feita consigo, divisão a divisão.`,
    categoriasTitle: 'Situações que resolvemos',
    categoriasSubtitle: 'O tipo de obra determina o método, o tempo e a forma de orçamentar',
    categorias: [
      {
        title: 'Como cobramos',
        icon: 'ri-money-euro-circle-line',
        description: 'Sem estimativa aberta. Antes de começar sabe exatamente quanto vai pagar e o que está incluído.',
        itens: [
          'Por m² — o mais comum em apartamentos e lojas',
          'Por empreitada — valor fechado para obras grandes',
          'Duas fases — limpeza grossa durante a obra e fina na entrega',
          'Orçamento gratuito, com visita ou por fotografias'
        ]
      },
      {
        title: 'Remodelação de apartamento',
        icon: 'ri-home-gear-line',
        description: 'O caso mais frequente: casa de banho e cozinha refeitas, pladur novo, pintura recente. Entregamos a casa pronta a mobilar.',
        itens: [
          'Limpeza fina divisão a divisão',
          'Armários e roupeiros por dentro',
          'Vidros, caixilhos e estores',
          'Normalmente um a dois dias'
        ]
      },
      {
        title: 'Loja ou escritório antes da abertura',
        icon: 'ri-store-3-line',
        description: 'Espaços comerciais têm data de abertura marcada e montagem de equipamento a decorrer até ao fim. Trabalhamos à volta disso, de noite se for preciso.',
        itens: [
          'Coordenação com montagem de mobiliário',
          'Montras e vidros sem marcas',
          'Trabalho noturno ou ao fim de semana',
          'Entrega pronta para stock e abertura'
        ]
      },
      {
        title: 'Empreendimento novo',
        icon: 'ri-building-4-line',
        description: 'Várias frações a entregar na mesma janela de tempo, com escadas e partes comuns a limpar no fim. Equipa dimensionada para o prazo.',
        itens: [
          'Equipa reforçada para cumprir a data',
          'Frações e partes comuns',
          'Faturação única para o promotor',
          'Vistoria de entrega acompanhada'
        ]
      }
    ],
    steps: [
      { icon: 'ri-camera-line', title: 'Visita ou fotos', description: 'Vemos o espaço presencialmente ou por fotografias, se o prazo for curto.' },
      { icon: 'ri-file-list-3-line', title: 'Orçamento fechado', description: 'Recebe o valor final por m² ou por empreitada, com o que está incluído.' },
      { icon: 'ri-brush-4-line', title: 'Limpeza por fases', description: 'Grossa e fina, de cima para baixo, com segunda passagem depois de o pó assentar.' },
      { icon: 'ri-check-double-line', title: 'Vistoria consigo', description: 'Percorremos o espaço divisão a divisão antes de dar o trabalho por concluído.' }
    ],
    faqs: [
      { question: 'Quanto tempo demora uma limpeza pós-obra?', answer: 'Um T2 remodelado fica normalmente pronto num dia. Um T3 ou T4 com obra profunda pode exigir dois dias. Lojas e escritórios dependem da área e da quantidade de vidro.' },
      { question: 'Retiram entulho?', answer: 'Retiramos entulho leve e embalagens deixadas em obra. Entulho pesado — sacos de escombro, sanitas antigas, portas — exige contentor e transporte próprio, que podemos orçamentar à parte.' },
      { question: 'Conseguem tirar tinta e cimento do pavimento sem danificar?', answer: 'Sim, desde que o produto seja o certo para o material. Identificamos o tipo de pavimento antes de começar: soalho flutuante, madeira maciça, pedra natural e microcimento tratam-se de forma diferente.' },
      { question: 'Podem limpar antes de a obra estar 100% terminada?', answer: 'Podemos, e em obras grandes é o mais eficiente. Fazemos uma limpeza grossa a meio, que melhora as condições de trabalho, e a limpeza fina só na entrega.' },
      { question: 'Trabalham ao fim de semana?', answer: 'Trabalhamos. Quando há data de entrega ou de abertura marcada, reservamos equipa para sábado ou domingo sem agravamento injustificado.' }
    ]
  },
  {
    id: 'limpeza-escritorios',
    slug: 'limpeza-escritorios',
    navTitle: 'Escritórios',
    formValue: 'Limpeza de Escritórios (Office)',
    title: 'Limpeza de Escritórios em Lisboa',
    subtitle: 'Office cleaning fora do horário de trabalho, com equipa fixa e discreta',
    description: 'Limpeza de escritórios em Lisboa, antes ou depois do expediente. Equipa fixa com acordo de confidencialidade, consumíveis incluídos e contrato mensal para empresas, coworkings e clínicas.',
    image: '/images/limpeza-escritorios.webp',
    imagePosition: 'center 35%',
    beneficios: [
      'Limpeza pós-laboral ou de madrugada — ninguém é incomodado',
      'Equipa fixa, identificada e com acordo de confidencialidade',
      'Consumíveis de casa de banho e copa incluídos no contrato',
      'Menos baixas por doença num espaço higienizado',
      'Contrato mensal com valor fixo e supervisão regular'
    ],
    features: [
      'Secretárias e superfícies de trabalho higienizadas',
      'Desinfeção de teclados, telefones e maçanetas',
      'Aspiração de alcatifas e lavagem de pavimentos',
      'Divisórias de vidro, portas e salas de reunião',
      'Casas de banho lavadas e desinfetadas',
      'Copa e zona de refeições, incluindo micro-ondas e frigorífico'
    ],
    fullContent: `Um escritório limpo nota-se — e sente-se. Não é só a imagem que dá a quem visita: é o ar que a equipa respira oito horas por dia, a alcatifa que acumula pó, o teclado partilhado, a maçaneta que toda a gente usa. A ALPHA CLEAN faz limpeza de escritórios em Lisboa (office cleaning) para empresas, coworkings, clínicas e consultórios, com equipas fixas e horários que não interferem com o trabalho.

A regra que seguimos é simples: a limpeza não deve ser vista. Trabalhamos em horário pós-laboral, entre as 19h e as 23h — o formato mais escolhido —, ou de madrugada, das 6h às 9h, para que a equipa encontre tudo limpo ao chegar. Em escritórios maiores, há ainda a opção de auxiliar fixo durante o expediente, que mantém copas, casas de banho e salas de reunião em ordem ao longo do dia.

O plano diário inclui limpeza e higienização de secretárias e superfícies de trabalho, desinfeção de pontos de contacto — teclados, ratos, telefones, maçanetas e interruptores —, aspiração de alcatifas e lavagem de pavimentos, limpeza de divisórias de vidro e portas, casas de banho lavadas e desinfetadas com reposição de consumíveis, copa e zona de refeições incluindo micro-ondas e frigorífico, despejo e separação de resíduos, e preparação das salas de reunião e da receção para o dia seguinte.

A confidencialidade é parte do serviço, não um extra. Os nossos técnicos assinam acordo de confidencialidade, a equipa é sempre a mesma e está identificada, e cumprimos as regras de acesso que a empresa definir: cartões, alarmes, zonas restritas ou salas que não devem ser abertas. Para escritórios com informação sensível — advogados, contabilidade, clínicas — este ponto costuma pesar mais do que o preço.

Além da rotina diária, fazemos trabalhos periódicos que a maioria dos contratos esquece: lavagem de cadeiras e alcatifas com injeção e extração, limpeza de vidros e divisórias em altura, e tratamento de odores em espaços fechados. Fazemos visita ao escritório antes de orçamentar e apresentamos um plano com valor mensal fixo — sem contagem de horas nem surpresas no fim do mês.`,
    categoriasTitle: 'Além da limpeza diária',
    categoriasSubtitle: 'Trabalhos periódicos que mantêm o escritório apresentável durante todo o ano',
    categorias: [
      {
        title: 'Horários disponíveis',
        icon: 'ri-time-line',
        description: 'Escolhe o horário que menos interfere com a operação. A limpeza deve notar-se no resultado, não no processo.',
        itens: [
          'Pós-laboral — entre as 19h e as 23h, o mais escolhido',
          'Madrugada — das 6h às 9h, para abrir com tudo limpo',
          'Presença diária — auxiliar fixo durante o expediente',
          'Fins de semana para limpezas profundas'
        ]
      },
      {
        title: 'Lavagem de cadeiras e alcatifas',
        icon: 'ri-sofa-line',
        description: 'A alcatifa de um open space absorve pó, ácaros e odores durante meses. Lavamos com injeção e extração, que trata a mancha e não só a superfície.',
        itens: [
          'Injeção e extração profissional',
          'Trata as manchas, não só a superfície',
          'Cadeiras de trabalho e sofás de receção',
          'Recomendado uma a duas vezes por ano'
        ]
      },
      {
        title: 'Vidros e divisórias',
        icon: 'ri-window-line',
        description: 'Divisórias de vidro e envidraçados de fachada marcam-se com dedadas e pó, e são a primeira coisa que um visitante vê.',
        itens: [
          'Divisórias interiores e salas de reunião',
          'Envidraçados de fachada',
          'Trabalho em altura com meios próprios',
          'Sem marcas nem película de produto'
        ]
      },
      {
        title: 'Desinfeção e controlo de odores',
        icon: 'ri-shield-cross-line',
        description: 'Em espaços fechados e com muita gente, a desinfeção de pontos de contacto reduz contágios e baixas. Tratamos também odores persistentes em copas e casas de banho.',
        itens: [
          'Desinfeção de pontos de contacto',
          'Tratamento de odores em copas e WC',
          'Produtos certificados e seguros',
          'Reforço em época de gripes'
        ]
      }
    ],
    steps: [
      { icon: 'ri-building-line', title: 'Visita ao escritório', description: 'Vemos a área, o tipo de pavimento, o número de postos e as casas de banho.' },
      { icon: 'ri-file-text-line', title: 'Plano e valor mensal', description: 'Recebe o plano diário, o horário e o valor fixo mensal, com consumíveis incluídos.' },
      { icon: 'ri-user-follow-line', title: 'Equipa dedicada', description: 'Equipa fixa, identificada e com acordo de confidencialidade assinado.' },
      { icon: 'ri-eye-line', title: 'Supervisão', description: 'Visitas de supervisão e um interlocutor direto para qualquer ajuste.' }
    ],
    faqs: [
      { question: 'Podem limpar fora do horário de trabalho?', answer: 'É o que fazemos na maioria dos contratos. Pós-laboral entre as 19h e as 23h ou de madrugada entre as 6h e as 9h. Cumprimos as regras de acesso, alarme e chaves que a empresa definir.' },
      { question: 'Os técnicos assinam acordo de confidencialidade?', answer: 'Assinam. É prática normal nos nossos contratos de escritório, e obrigatória em clínicas, escritórios de advogados e empresas de contabilidade.' },
      { question: 'Os consumíveis das casas de banho estão incluídos?', answer: 'Podem ser incluídos no valor mensal — papel, sabão, gel desinfetante e sacos — ou ficar a cargo da empresa. Dizemos-lhe qual das opções sai mais barata no seu caso.' },
      { question: 'Trabalham com coworkings e clínicas?', answer: 'Sim. Coworkings exigem limpezas mais frequentes de zonas comuns; clínicas e consultórios exigem protocolos de desinfeção próprios. Adaptamos o plano a cada caso.' },
      { question: 'Qual é o valor de uma limpeza de escritório?', answer: 'Depende da área, do número de casas de banho, do tipo de pavimento e da frequência. Fazemos visita e apresentamos valor mensal fechado, gratuitamente e sem compromisso.' }
    ]
  },
  {
    id: 'limpeza-residencial',
    slug: 'limpeza-residencial',
    navTitle: 'Residencial',
    formValue: 'Limpeza Residencial / Doméstica',
    title: 'Limpeza Residencial e Doméstica em Lisboa',
    subtitle: 'Limpeza doméstica regular ou limpeza profunda, com a mesma pessoa de confiança',
    description: 'Limpeza doméstica em Lisboa, semanal, quinzenal ou pontual. Sempre a mesma profissional, produtos incluídos, limpeza profunda e limpeza de mudança de casa. Empresa com seguro.',
    image: '/images/limpeza-residencial.webp',
    imagePosition: 'center 40%',
    beneficios: [
      'É sempre a mesma profissional — ganha confiança e rotina',
      'Semanal, quinzenal ou pontual, conforme precisar',
      'Produtos e equipamento incluídos',
      'Empresa com seguro — não é trabalho informal',
      'Fatura com NIF e possibilidade de dedução'
    ],
    features: [
      'Aspiração e lavagem de todos os pavimentos',
      'Casas de banho lavadas e desinfetadas',
      'Cozinha completa, incluindo placa, exaustor e frentes',
      'Limpeza do pó em móveis e prateleiras',
      'Mudança de roupa de cama e feitura das camas',
      'Despejo de lixos e arrumação de espaços comuns'
    ],
    fullContent: `A casa tratada como deve ser, sem lhe ocupar o fim de semana. A ALPHA CLEAN faz limpeza doméstica em Lisboa para apartamentos e moradias, em regime regular — semanal ou quinzenal — ou em intervenções pontuais, como uma limpeza profunda ou uma limpeza de mudança de casa.

O ponto que mais distingue o nosso serviço é a continuidade: é sempre a mesma profissional a ir a sua casa. Isso muda tudo. Quem já conhece a casa sabe onde ficam as coisas, sabe que produto usar naquela bancada, repara que o vidro do duche precisa de atenção esta semana. E, do seu lado, deixa de ter de explicar tudo de novo a cada visita — que é, no fundo, o que leva a maior parte das pessoas a desistir de contratar limpeza.

Uma limpeza regular inclui aspiração e lavagem de todos os pavimentos, casas de banho lavadas e desinfetadas, cozinha completa incluindo placa, exaustor e frentes de armário, limpeza do pó em móveis, prateleiras e equipamentos, mudança de roupa de cama e feitura das camas, limpeza de espelhos e vidros interiores, despejo de lixos com reposição de sacos, e arrumação leve dos espaços comuns. Passagem a ferro e limpeza de varandas e marquises podem ser incluídas quando contratadas.

A limpeza profunda é outro serviço. Ocupa um dia completo e serve para pôr a casa em dia: interior de armários e roupeiros, eletrodomésticos por dentro, rodapés, portas, interruptores, azulejos, calhas de janela e zonas que a rotina semanal não alcança. É o que recomendamos antes de começar um contrato regular, ou uma a duas vezes por ano em casas já acompanhadas. A limpeza de mudança — entrada ou saída de arrendamento — segue a mesma lógica, com a casa vazia, e costuma ser decisiva para recuperar a caução.

Trabalhamos com empresa e com seguro, e emitimos fatura com NIF. Não é trabalho informal: se algo se partir, a responsabilidade é nossa e está coberta. Pode estar ou não em casa durante a limpeza — a maioria dos nossos clientes regulares dá-nos chave depois de ganhar confiança, mas isso é sempre uma decisão sua e nunca uma condição.`,
    categoriasTitle: 'Serviços adicionais em casa',
    categoriasSubtitle: 'Coisas que se vão adiando e que resolvemos numa única intervenção',
    categorias: [
      {
        title: 'Formatos mais pedidos',
        icon: 'ri-calendar-line',
        description: 'Três formas de trabalhar, consoante o que precisa agora. Pode começar por uma limpeza profunda e só depois decidir a rotina.',
        itens: [
          'Regular — 3 a 4 horas por semana, a mesma profissional',
          'Profunda — um dia completo para pôr a casa em dia',
          'Mudança — limpeza de entrada ou saída de arrendamento',
          'Pontual — antes ou depois de receber gente em casa'
        ]
      },
      {
        title: 'Tratamento de roupa',
        icon: 'ri-t-shirt-line',
        description: 'Lavar é fácil; passar é o que ninguém quer fazer. Podemos incluir passagem a ferro no tempo da limpeza ou reservar uma visita só para isso.',
        itens: [
          'Passagem a ferro incluída no serviço',
          'Máquina posta e roupa estendida',
          'Arrumação de roupa lavada',
          'Visita dedicada só a tratamento de roupa'
        ]
      },
      {
        title: 'Lavagem de sofás e colchões',
        icon: 'ri-sofa-line',
        description: 'Sofás e colchões acumulam ácaros, pó e odores que a aspiração normal não tira. Lavamos com injeção e extração, com secagem no próprio dia.',
        itens: [
          'Injeção e extração profissional',
          'Sofás, cadeirões e cabeceiras',
          'Colchões e tratamento antiácaros',
          'Seca no próprio dia'
        ]
      },
      {
        title: 'Vidros, estores e marquises',
        icon: 'ri-window-2-line',
        description: 'As janelas por dentro e por fora, as calhas cheias de pó e os estores que nunca ninguém limpa. Trabalho de meia jornada que muda a luz da casa.',
        itens: [
          'Vidros por dentro e por fora',
          'Calhas, caixilhos e rufos',
          'Estores e persianas lâmina a lâmina',
          'Marquises e varandas lavadas'
        ]
      }
    ],
    steps: [
      { icon: 'ri-chat-3-line', title: 'Fala connosco', description: 'Diz-nos a tipologia da casa, o que precisa e com que frequência.' },
      { icon: 'ri-mail-send-line', title: 'Proposta em 24h', description: 'Recebe o valor por visita ou mensal, com o que está incluído.' },
      { icon: 'ri-sparkling-2-line', title: 'Primeira limpeza', description: 'Acompanhamos a primeira visita para acertar prioridades e preferências.' },
      { icon: 'ri-repeat-line', title: 'Rotina montada', description: 'Fica com dia e hora fixos e sempre a mesma profissional.' }
    ],
    faqs: [
      { question: 'Tenho de estar em casa durante a limpeza?', answer: 'Não. Pode estar, se preferir, sobretudo nas primeiras visitas. Muitos clientes regulares deixam chave depois de ganhar confiança — mas isso é sempre decisão sua.' },
      { question: 'Os produtos e os equipamentos são vossos?', answer: 'São, e estão incluídos no preço. Se preferir produtos específicos, por alergias ou por preferência de material, usamos os seus sem qualquer problema.' },
      { question: 'É sempre a mesma pessoa?', answer: 'Sim, é esse o princípio do serviço. Em caso de férias ou doença avisamos com antecedência e enviamos alguém já informado sobre a sua casa.' },
      { question: 'Qual a diferença entre limpeza regular e limpeza profunda?', answer: 'A regular mantém a casa em dia em três a quatro horas. A profunda ocupa um dia completo e trata o que a rotina não alcança: interior de armários, eletrodomésticos por dentro, rodapés, azulejos e calhas.' },
      { question: 'Trabalham com seguro?', answer: 'Trabalhamos. Somos uma empresa com seguro de responsabilidade civil e emitimos sempre fatura com NIF.' }
    ]
  },
  {
    id: 'limpeza-alojamento-local',
    slug: 'limpeza-alojamento-local',
    navTitle: 'Alojamento Local',
    formValue: 'Limpeza de Alojamento Local',
    title: 'Limpeza de Alojamento Local em Lisboa',
    subtitle: 'Turnovers entre check-out e check-in, com roupa tratada e fotos de confirmação',
    description: 'Limpeza de alojamento local e Airbnb em Lisboa. Turnover no mesmo dia entre check-out e check-in, lavandaria de roupa de cama e banho, reposição de amenities e relatório com fotos.',
    image: '/images/limpeza-alojamento-local.webp',
    imagePosition: 'center 25%',
    beneficios: [
      'Turnover garantido entre o check-out e o check-in do mesmo dia',
      'Lavandaria de roupa de cama e banho incluída',
      'Relatório com fotos após cada limpeza',
      'Registo de danos e esquecimentos dos hóspedes',
      'Preço fixo por turnover, sem mensalidade'
    ],
    features: [
      'Limpeza completa de todas as divisões entre hóspedes',
      'Muda e colocação de roupa de cama e toalhas',
      'Lavandaria com recolha e entrega',
      'Reposição de amenities e produtos de boas-vindas',
      'Higienização de casas de banho e cozinha',
      'Arrumação e preparação do espaço para fotografia'
    ],
    fullContent: `No alojamento local, a limpeza é o que decide a sua avaliação. Um hóspede perdoa uma vista pior ou um chuveiro fraco; não perdoa um cabelo na banheira nem lençóis que não cheiram a lavado. A ALPHA CLEAN faz limpeza de alojamento local em Lisboa para anfitriões de Airbnb, Booking e plataformas equivalentes, com turnovers garantidos entre check-out e check-in.

O turnover é uma operação com relógio. Entre a saída de um hóspede e a entrada do seguinte há muitas vezes três ou quatro horas para limpar tudo, mudar a roupa, repor consumíveis e deixar o apartamento apresentável. Trabalhamos com agenda sincronizada com as suas reservas, o que significa que não tem de nos avisar reserva a reserva: sabemos quando o apartamento vazia e quando volta a ser ocupado.

Cada turnover inclui limpeza completa de todas as divisões, muda e colocação de roupa de cama e toalhas, lavandaria da roupa suja com recolha e entrega, reposição de amenities, papel e produtos de boas-vindas, higienização de casas de banho e cozinha, verificação e limpeza de louça e eletrodomésticos, arrumação e preparação do espaço tal como aparece nas fotografias do anúncio, despejo de lixos com separação de resíduos, e registo de danos, faltas ou objetos esquecidos pelos hóspedes.

No fim de cada limpeza recebe um relatório com fotografias. Isto resolve dois problemas de uma vez: dá-lhe a certeza de que o apartamento está pronto sem ter de lá ir, e dá-lhe prova em caso de reclamação ou de pedido de acionamento de caução por danos. Para quem gere unidades à distância, é a diferença entre confiar e verificar.

Trabalhamos com anfitriões de uma única unidade e com gestores de vários apartamentos. Cobramos preço fixo por turnover, definido por tipologia, sem mensalidade nem fidelização — paga o que usa. Para unidades com ocupação alta, recomendamos um kit de reserva de roupa em cada apartamento, que elimina a dependência do tempo de lavandaria e evita o cenário em que uma reserva de última hora fica sem lençóis limpos.`,
    categoriasTitle: 'Serviços para anfitriões',
    categoriasSubtitle: 'Tudo o que é preciso para não ter de estar presente em cada mudança de hóspede',
    categorias: [
      {
        title: 'Como funciona',
        icon: 'ri-key-2-line',
        description: 'Preço por turnover e agenda ligada às suas reservas. Sem mensalidade e sem ter de avisar a cada entrada e saída.',
        itens: [
          'Preço fixo por turnover, por tipologia',
          'Agenda sincronizada com as reservas',
          'Sem mensalidade nem fidelização',
          'Kit de reserva de roupa em cada unidade'
        ]
      },
      {
        title: 'Lavandaria de roupa de cama e banho',
        icon: 'ri-shirt-line',
        description: 'Recolhemos a roupa suja, lavamos a temperatura alta e devolvemos pronta a usar. O anfitrião deixa de ter máquinas a trabalhar entre reservas.',
        itens: [
          'Recolha e entrega incluídas',
          'Lavagem a temperatura alta',
          'Roupa dobrada e pronta a colocar',
          'Substituição de peças danificadas mediante aviso'
        ]
      },
      {
        title: 'Apoio a check-in e gestão de chaves',
        icon: 'ri-door-lock-line',
        description: 'Guardamos chaves, verificamos o apartamento antes da chegada e avisamos quando está pronto. Para quem gere à distância, é o que permite dormir descansado.',
        itens: [
          'Guarda e gestão de chaves',
          'Verificação antes da chegada',
          'Aviso de apartamento pronto',
          'Registo de esquecimentos dos hóspedes'
        ]
      },
      {
        title: 'Limpeza profunda sazonal',
        icon: 'ri-sparkling-line',
        description: 'Duas a três vezes por ano, entre reservas, uma limpeza que trata o que o turnover não permite: armários, eletrodomésticos por dentro, colchões e estofos.',
        itens: [
          'Interior de armários e eletrodomésticos',
          'Lavagem de colchões e sofás',
          'Vidros, estores e marquises',
          'Agendada num intervalo sem reservas'
        ]
      }
    ],
    steps: [
      { icon: 'ri-home-heart-line', title: 'Conhecemos a unidade', description: 'Visitamos o apartamento e registamos a tipologia, o stock de roupa e o padrão de arrumação.' },
      { icon: 'ri-price-tag-3-line', title: 'Preço por turnover', description: 'Fica com valor fixo por limpeza, com lavandaria e amenities discriminados.' },
      { icon: 'ri-calendar-check-line', title: 'Agenda sincronizada', description: 'Ligamo-nos ao seu calendário de reservas — não precisa de avisar em cada mudança.' },
      { icon: 'ri-image-line', title: 'Fotos e confirmação', description: 'Recebe relatório com fotografias assim que o apartamento fica pronto.' }
    ],
    faqs: [
      { question: 'Conseguem limpar entre o check-out e o check-in no mesmo dia?', answer: 'É o serviço base. Trabalhamos com a janela entre saída e entrada e confirmamos com fotografias assim que o apartamento fica pronto.' },
      { question: 'Tratam da roupa de cama e toalhas?', answer: 'Tratamos. Recolhemos a roupa suja, lavamos a temperatura alta e devolvemos pronta a colocar. Para ocupação alta recomendamos kit de reserva em cada unidade.' },
      { question: 'O que acontece se os hóspedes deixarem o apartamento muito sujo?', answer: 'Fotografamos, avisamos de imediato e dizemos-lhe quanto custa a limpeza extra antes de avançar. As fotografias servem de prova em pedido de acionamento de caução.' },
      { question: 'Gerem várias unidades?', answer: 'Sim. Trabalhamos com gestores de vários apartamentos, com faturação única e um interlocutor só.' },
      { question: 'Repõem os amenities?', answer: 'Repomos: papel, sabonetes, gel, champô e produtos de boas-vindas. Pode fornecer o stock ou pedir que tratemos da compra, com o custo a ser refletido na fatura.' }
    ]
  },
  {
    id: 'limpeza-restaurantes',
    slug: 'limpeza-restaurantes',
    navTitle: 'Restaurantes',
    formValue: 'Limpeza de Restaurantes e Cozinhas',
    title: 'Limpeza de Restaurantes e Cozinhas em Lisboa',
    subtitle: 'Sala, cozinha e zonas técnicas prontas para inspeção, com registos HACCP',
    description: 'Limpeza de restaurantes em Lisboa: desengorduramento de cozinha, hottes, filtros e condutas de extração, sala e casas de banho. Trabalho noturno e folhas de registo para o plano HACCP.',
    image: '/images/limpeza-restaurantes.webp',
    imagePosition: 'center 40%',
    beneficios: [
      'Trabalho noturno ou de madrugada, sem parar o serviço',
      'Registos de limpeza para o plano HACCP',
      'Desengorduramento profissional de zona de confeção',
      'Redução do risco de incêndio em hottes e condutas',
      'Espaço preparado para inspeção a qualquer momento'
    ],
    features: [
      'Desengorduramento de fogões, fritadeiras, grelhas e chapas',
      'Limpeza de hottes, filtros e condutas de extração',
      'Lavagem de bancadas e superfícies em inox',
      'Higienização de câmaras de frio e arcas',
      'Pavimentos antiderrapantes e ralos',
      'Sala, mesas, balcão, montras e casas de banho'
    ],
    fullContent: `Higiene de cozinha não é um detalhe de apresentação: é uma condição de licenciamento. Numa inspeção, o que está por trás do fogão conta tanto como o que está no prato. A ALPHA CLEAN faz limpeza de restaurantes, cafés, pastelarias e cozinhas profissionais em Lisboa, com trabalho executado depois do fecho e registos que alimentam o plano HACCP do estabelecimento.

A limpeza de restauração divide-se em três frentes com ritmos diferentes. A diária trata da sala, das casas de banho, dos pavimentos e das superfícies após o fecho. A semanal faz o desengorduramento profundo da zona de confeção — fogões, fritadeiras, grelhas, chapas, bancadas e azulejos —, que é onde a gordura se acumula e endurece. A trimestral trata das hottes, filtros e condutas de extração, que exigem desmontagem e produto específico.

Esta última frente é a mais esquecida e a mais crítica. Uma conduta de extração com gordura acumulada é um risco de incêndio real e é dos primeiros pontos que uma inspeção verifica. Desmontamos os filtros, lavamos por imersão, limpamos o interior acessível da conduta e deixamos registo escrito da intervenção com data — que é exatamente o que lhe pedem quando há uma verificação.

O serviço inclui também lavagem de bancadas, lava-louças e superfícies em inox, higienização de câmaras de frio e arcas, lavagem de pavimentos antiderrapantes e desobstrução de ralos, limpeza de azulejos, paredes laváveis e tetos, tratamento da sala com mesas, cadeiras, balcão e montras, casas de banho de clientes e de pessoal, e desinfeção da zona de resíduos e contentores. Usamos produtos próprios para zonas de manipulação de alimentos, com ficha técnica disponível.

No fim de cada intervenção entregamos folha de registo de limpeza assinada, pronta a arquivar no dossier HACCP. Trabalhamos com contratos regulares e também com limpezas pontuais — uma limpeza profunda antes de uma abertura, ou depois de uma notificação de inspeção. Visitamos o espaço após o serviço, para ver a cozinha em condições reais, e apresentamos plano e orçamento sem compromisso.`,
    categoriasTitle: 'Intervenções especializadas',
    categoriasSubtitle: 'O que uma cozinha profissional precisa além da limpeza do fim do turno',
    categorias: [
      {
        title: 'Frequências típicas',
        icon: 'ri-calendar-2-line',
        description: 'Cada frente tem o seu ritmo. O plano combina as três para que nada fique por fazer entre inspeções.',
        itens: [
          'Diária — sala, casas de banho e pavimentos após o fecho',
          'Semanal — desengorduramento profundo da zona de confeção',
          'Trimestral — hottes, filtros e condutas de extração',
          'Pontual — antes de abertura ou de inspeção'
        ]
      },
      {
        title: 'Hottes, filtros e condutas',
        icon: 'ri-fire-line',
        description: 'A gordura acumulada na extração é risco de incêndio e é dos primeiros pontos verificados numa inspeção. Desmontamos, lavamos por imersão e deixamos registo com data.',
        itens: [
          'Desmontagem de filtros',
          'Lavagem por imersão',
          'Limpeza do interior acessível da conduta',
          'Registo escrito da intervenção'
        ]
      },
      {
        title: 'Câmaras de frio e equipamentos',
        icon: 'ri-fridge-line',
        description: 'Câmaras, arcas e equipamentos de frio exigem higienização com produto próprio e sem interromper a cadeia de frio mais do que o necessário.',
        itens: [
          'Higienização de câmaras e arcas',
          'Limpeza de borrachas e drenagens',
          'Produto próprio para zona alimentar',
          'Intervenção rápida para não quebrar o frio'
        ]
      },
      {
        title: 'Apoio ao plano HACCP',
        icon: 'ri-clipboard-line',
        description: 'Entregamos folha de registo assinada por intervenção, com o que foi feito e quando. É o documento que o inspetor pede e que muitos estabelecimentos não têm.',
        itens: [
          'Folha de registo por intervenção',
          'Fichas técnicas dos produtos usados',
          'Plano de higienização por zona',
          'Apoio em caso de inspeção'
        ]
      }
    ],
    steps: [
      { icon: 'ri-restaurant-2-line', title: 'Visita após o serviço', description: 'Vemos a cozinha em condições reais de funcionamento, não depois de arrumada.' },
      { icon: 'ri-file-list-3-line', title: 'Plano de higienização', description: 'Definimos o que é diário, semanal e trimestral, com valor mensal fechado.' },
      { icon: 'ri-moon-line', title: 'Execução noturna', description: 'Trabalhamos após o fecho ou de madrugada, sem interferir com o serviço.' },
      { icon: 'ri-checkbox-circle-line', title: 'Registo assinado', description: 'Recebe folha de registo pronta a arquivar no dossier HACCP.' }
    ],
    faqs: [
      { question: 'Trabalham depois do fecho do restaurante?', answer: 'Trabalhamos. A maioria dos nossos contratos de restauração é executada depois do fecho ou de madrugada, precisamente para não interferir com o serviço.' },
      { question: 'Os produtos são próprios para zonas de alimentos?', answer: 'São, e temos ficha técnica de cada um disponível para o seu dossier HACCP. É um dos documentos que costumam ser pedidos em inspeção.' },
      { question: 'Fazem limpeza de hottes e condutas de extração?', answer: 'Fazemos, com desmontagem de filtros e lavagem por imersão. Recomendamos intervenção trimestral, ou mais frequente em cozinhas com muita fritura.' },
      { question: 'Dão apoio em caso de inspeção da ASAE?', answer: 'Damos. Entregamos registos de limpeza assinados e fichas técnicas dos produtos, e conseguimos agendar uma limpeza profunda com urgência após notificação.' },
      { question: 'Fazem limpezas pontuais ou só contratos?', answer: 'Fazemos as duas coisas. Há quem nos chame só para uma limpeza profunda antes da abertura ou depois de uma notificação, e depois decida se quer contrato regular.' }
    ]
  },
  {
    id: 'limpeza-lojas-comercio',
    slug: 'limpeza-lojas-comercio',
    navTitle: 'Lojas e Comércio',
    formValue: 'Limpeza de Lojas e Espaços Comerciais',
    title: 'Limpeza de Lojas e Espaços Comerciais em Lisboa',
    subtitle: 'Lojas, ginásios, clínicas e showrooms prontos a abrir portas todos os dias',
    description: 'Limpeza de lojas e espaços comerciais em Lisboa: retalho, ginásios, clínicas, salões e showrooms. Limpeza antes da abertura, montras sem marcas e contrato mensal flexível.',
    image: '/images/limpeza-lojas-comercio.webp',
    imagePosition: 'center 40%',
    beneficios: [
      'Limpeza feita antes de abrir portas ao público',
      'Montras e vidros sem marcas nem película',
      'Contrato mensal flexível, incluindo campanhas curtas',
      'Desinfeção de equipamento de uso partilhado',
      'Consumíveis de casas de banho incluídos se quiser'
    ],
    features: [
      'Montras e vidros interiores e exteriores',
      'Lavagem e aspiração de pavimentos e alcatifas',
      'Expositores, prateleiras e mobiliário',
      'Desinfeção de balcões, terminais e maçanetas',
      'Provadores, espelhos e zonas de atendimento',
      'Casas de banho, armazém e vestiários'
    ],
    fullContent: `Quem entra numa loja decide nos primeiros dez segundos. Um pavimento marcado, uma montra com dedadas ou um provador com pó dizem ao cliente, antes de qualquer vendedor falar, o cuidado que aquele espaço tem. A ALPHA CLEAN faz limpeza de lojas e espaços comerciais em Lisboa: retalho de rua e de centro comercial, ginásios, clínicas, salões de estética e showrooms.

A regra do setor é limpar antes de abrir. Trabalhamos ao início da manhã, antes da chegada dos clientes, ou depois do fecho, consoante o horário do espaço e as regras do centro comercial. O plano regular inclui montras e vidros interiores e exteriores sem marcas, lavagem e aspiração de pavimentos e alcatifas, limpeza de expositores, prateleiras e mobiliário, desinfeção de balcões, terminais de pagamento e maçanetas, provadores, espelhos e zonas de atendimento, casas de banho de clientes e de pessoal, zona de armazém e vestiários, despejo de lixos com gestão de cartão e embalagens, e limpeza da entrada, tapetes e fachada imediata.

Cada setor tem um ponto crítico próprio. No retalho são as montras e os provadores. Nos ginásios e estúdios é o equipamento de uso partilhado, os vestiários e o controlo de odores — aqui a desinfeção não é estética, é o que mantém os sócios. Nas clínicas e salões são os protocolos de desinfeção e a gestão de resíduos específicos. Nos showrooms é o vidro, o brilho do pavimento e a ausência de pó no produto exposto. Montamos o plano a partir desse ponto crítico, não de uma lista genérica.

As montras merecem uma nota à parte, porque são a queixa mais frequente de quem já contratou limpeza antes: vidros que ao sol da tarde mostram todas as marcas e uma película baça. Isso resulta de produto a mais e de material errado. Trabalhamos com técnica e material próprios para vidro grande, sem deixar resíduo — e é uma das coisas que se nota logo na primeira semana.

Fazemos contratos mensais com flexibilidade real, incluindo reforços em época alta e contratos curtos para lojas de campanha ou pop-ups. Visitamos o espaço, apresentamos proposta com valor mensal fechado e mantemos supervisão regular para que a qualidade não caia ao terceiro mês — que é, tipicamente, quando cai.`,
    categoriasTitle: 'Pontos que tratamos com atenção extra',
    categoriasSubtitle: 'Cada tipo de espaço comercial tem um ponto crítico próprio — é por aí que começamos',
    categorias: [
      {
        title: 'Setores que servimos',
        icon: 'ri-store-2-line',
        description: 'O plano muda conforme o espaço: o que é crítico numa loja de roupa não é o mesmo que num ginásio ou numa clínica.',
        itens: [
          'Retalho — lojas de rua e em centro comercial',
          'Saúde e bem-estar — ginásios, clínicas e salões',
          'Showrooms — automóvel, mobiliário e decoração',
          'Contratos curtos para pop-ups e campanhas'
        ]
      },
      {
        title: 'Ginásios e estúdios',
        icon: 'ri-run-line',
        description: 'Equipamento partilhado, balneários e odores. A desinfeção aqui não é questão de imagem: é o que mantém os sócios inscritos.',
        itens: [
          'Equipamento desinfetado ponto a ponto',
          'Balneários, duches e cacifos',
          'Controlo de odores e humidade',
          'Limpeza antes da abertura ou após o fecho'
        ]
      },
      {
        title: 'Montras e vidros',
        icon: 'ri-window-line',
        description: 'A queixa mais comum de quem já teve outra empresa: montras que ao sol mostram todas as marcas. Material e técnica próprios para vidro grande.',
        itens: [
          'Sem película nem resíduo de produto',
          'Vidro interior e exterior',
          'Vinis e lettering tratados com cuidado',
          'Reforço em dias de chuva e pó'
        ]
      },
      {
        title: 'Clínicas e salões',
        icon: 'ri-heart-pulse-line',
        description: 'Espaços de saúde e estética exigem protocolo de desinfeção, separação de resíduos e discrição total no horário de atendimento.',
        itens: [
          'Protocolos de desinfeção por zona',
          'Gestão de resíduos específicos',
          'Limpeza entre atendimentos, se necessário',
          'Equipa fixa com confidencialidade'
        ]
      }
    ],
    steps: [
      { icon: 'ri-store-3-line', title: 'Visita ao espaço', description: 'Vemos a área, o pavimento, os vidros e o horário de funcionamento.' },
      { icon: 'ri-file-text-line', title: 'Proposta mensal', description: 'Valor mensal fechado, com frequência e horário definidos.' },
      { icon: 'ri-sun-line', title: 'Rotina antes de abrir', description: 'Trabalhamos antes da abertura ou após o fecho, conforme o espaço.' },
      { icon: 'ri-eye-line', title: 'Supervisão regular', description: 'Visitas de controlo para a qualidade não cair ao terceiro mês.' }
    ],
    faqs: [
      { question: 'Conseguem limpar antes de a loja abrir?', answer: 'Sim, é o horário mais pedido. Trabalhamos ao início da manhã ou depois do fecho, respeitando as regras de acesso do centro comercial quando aplicável.' },
      { question: 'Trabalham com ginásios e clínicas?', answer: 'Trabalhamos. Em ginásios o foco é equipamento partilhado, balneários e odores; em clínicas há protocolos de desinfeção e separação de resíduos que cumprimos.' },
      { question: 'E as montras? Ficam com marcas ao sol?', answer: 'Não, se o material e a técnica forem os corretos. As marcas resultam quase sempre de excesso de produto. Usamos material próprio para vidro grande, sem deixar resíduo.' },
      { question: 'Fazem contratos de curta duração?', answer: 'Fazemos. Lojas de campanha, pop-ups e espaços sazonais podem ter contratos de um a três meses, sem penalização.' },
      { question: 'Repõem os consumíveis das casas de banho?', answer: 'Se quiser, sim. Podemos incluir papel, sabão e gel desinfetante no valor mensal ou deixar essa parte a seu cargo.' }
    ]
  }
];

// Mapa por id, no formato que a página /servico/:id consome
export const limpezaServices: Record<string, LimpezaService> =
  limpezaServicesList.reduce((acc, service) => {
    acc[service.id] = service;
    return acc;
  }, {} as Record<string, LimpezaService>);

export default limpezaServices;
